import { createContext, useState, useEffect, PropsWithChildren } from 'react'
import { NextRouter, useRouter } from 'next/router'
import { parseQueryFromString } from '../../utils/query'
import { limitSpecialists } from '../../constants/profile'
import { SearchSpecialistsProviderProps, SearchSpecialistsFilters, SearchSpecialistsState } from './types'
import { filteringProfileDataByPlan } from '../../utils/filteringDataByPlan'
import { PracticeSpecialist } from '../../types'
import { getPracticeSpecialists } from '../../api/practice'
import getStatisticTreatments from '../../utils/getStatisticTreatments'
import getLanguageFromLocale from '../../utils/getLanguageFromLocale'

const SearchSpecialistsContext = createContext<SearchSpecialistsState>({
  state: {
    specialists: [],
    filters: {
      more: 1,
      sort: 'popular',
      search: '',
      keyword: ''
    },
    initiallyEmpty: true,
    isLoading: false,
    totalCount: 0,
    canLoadNext: true
  },
  changeMore: (more) => more,
  changeSort: (sort) => sort,
  changeSearch: (search) => search,
  changeKeyword: (keyword) => keyword
})

const getDefaultStateFromRouter = (
  router: NextRouter,
  defaultValues?: Record<string, number | string>
): SearchSpecialistsFilters => {
  const { search = '', sort = 'popular', keyword = '' } = parseQueryFromString(router.asPath)
  return {
    more: Number(defaultValues?.page) || Number(defaultValues?.more || router.query.more || 1),
    search: defaultValues?.search ? String(defaultValues.search) : search,
    sort: (defaultValues?.sort || sort) as SearchSpecialistsFilters['sort'],
    keyword: (defaultValues?.keyword || keyword) as SearchSpecialistsFilters['keyword']
  }
}

function SearchSpecialistsProvider({
  children,
  specialists: initialSpecialists,
  defaultFilters,
  totalCount: initialTotalCount,
  limit = limitSpecialists,
  forceUpdate = false
}: PropsWithChildren<SearchSpecialistsProviderProps>) {
  const router = useRouter()

  const [state, setState] = useState<SearchSpecialistsState['state']>({
    specialists: initialSpecialists || [],
    filters: getDefaultStateFromRouter(router, defaultFilters),
    totalCount: initialTotalCount || 0,
    initiallyEmpty: !initialTotalCount,
    canLoadNext: initialTotalCount > limit,
    isLoading: false
  })

  const language = getLanguageFromLocale(router.locale as string)

  useEffect(() => {
    const loadSpecialistsFromApi = async () => {
      const { result: specialists = [], totalCount = 0 } = await getPracticeSpecialists({
        slug: router.query?.slug as string,
        lang: router.locale as string,
        offset: (state.filters.more - 1) * limit,
        limit,
        sort: state.filters.sort,
        search: state.filters.search,
        keyword: state.filters.keyword
      })

      const specialistsWithFilteredData = specialists.map((spec: PracticeSpecialist) => {
        const normalizedSpecialist = filteringProfileDataByPlan({ practiceSpecialist: spec })

        const treatments = getStatisticTreatments(spec.statistic, language)

        return {
          ...normalizedSpecialist,
          treatments
        }
      })
      const shouldReplace = forceUpdate || state.filters.more === 1
      setState((prev) => ({
        ...prev,
        specialists: (shouldReplace
          ? specialistsWithFilteredData
          : [...prev.specialists, ...specialistsWithFilteredData]) as PracticeSpecialist[],
        totalCount,
        isLoading: false,
        canLoadNext: shouldReplace
          ? totalCount > specialists.length
          : totalCount > [...prev.specialists, ...specialists].length
      }))
    }

    if (state.isLoading) {
      loadSpecialistsFromApi()
    }
    // todo: clarify deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isLoading])

  const changeMore = (more: number) =>
    setState((prev) => ({ ...prev, isLoading: true, filters: { ...prev.filters, more } }))

  const changeSort = (sort: string) =>
    setState((prev) => ({
      ...prev,
      isLoading: true,
      filters: { ...prev.filters, sort: sort as SearchSpecialistsFilters['sort'], more: 1 }
    }))

  const changeSearch = (search: string) =>
    setState((prev) => ({
      ...prev,
      isLoading: true,
      filters: { ...prev.filters, search, more: 1 }
    }))

  const changeKeyword = (keyword: string) =>
    setState((prev) => ({
      ...prev,
      isLoading: true,
      filters: { ...prev.filters, keyword, more: 1 }
    }))

  return (
    <SearchSpecialistsContext.Provider
      value={{
        state,
        changeSort,
        changeSearch,
        changeKeyword,
        changeMore
      }}
    >
      {children}
    </SearchSpecialistsContext.Provider>
  )
}

export { SearchSpecialistsProvider, SearchSpecialistsContext }
